import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IapConsumableButton from "@/components/IapConsumableButton";
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import ProUpsellBanner from "@/components/ProUpsellBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusIcon as Plus, TrashIcon as Trash, InfoIcon as Info, DogIcon as Dog } from "@phosphor-icons/react";
import AddBreederDialog from "@/components/AddBreederDialog";
import { formatApiError } from "@/lib/utils-app";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import DogPicker from "@/components/DogPicker";
import BreedPicker from "@/components/BreedPicker";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import { toast } from "sonner";

export default function LitterForm() {
  const navigate = useNavigate();
  const [breeders, setBreeders] = useState([]);
  const [showAddBreeder, setShowAddBreeder] = useState(false);
  const [sire, setSire] = useState("");
  const [dam, setDam] = useState("");
  const [sireObj, setSireObj] = useState(null);
  const [damObj, setDamObj] = useState(null);
  const [whelpDate, setWhelpDate] = useState("");
  const [breederId, setBreederId] = useState("none");
  const [notes, setNotes] = useState("");
  const [puppies, setPuppies] = useState([
    { name: "", sex: "male", color: "", breed: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [promo, setPromo] = useState(null);
  // Whether the user's plan covers a free litter registration. Same
  // contract as DogForm — backend gating is authoritative; this is
  // purely UI honesty so we don't show "Pay $75" to a Pro user.
  const [coversLitter, setCoversLitter] = useState(false);

  useEffect(() => {
    api.get("/breeders").then((r) => setBreeders(r.data));
    api.get("/billing/my-subscription")
      .then((r) => setCoversLitter(!!r.data?.covers?.one_time_litter))
      .catch(() => setCoversLitter(false));
  }, []);

  // Default breed is the sire's breed — when the user picks a sire, pre-fill
  // it on every puppy row that hasn't been manually edited yet. We detect
  // "manually edited" by comparing each row's breed to the *previous* sire
  // breed, so switching sires never clobbers explicit per-pup values.
  const prevSireBreedRef = React.useRef("");
  useEffect(() => {
    const sireBreed = (sireObj?.breed || "").trim();
    if (!sireBreed) return;
    const prev = prevSireBreedRef.current;
    setPuppies((rows) =>
      rows.map((p) => (p.breed === "" || p.breed === prev ? { ...p, breed: sireBreed } : p))
    );
    prevSireBreedRef.current = sireBreed;
  }, [sireObj?.breed]);

  const defaultBreed = (sireObj?.breed || "").trim();
  const addPuppy = () =>
    setPuppies([...puppies, { name: "", sex: "male", color: "", breed: defaultBreed }]);
  const updatePup = (i, k, v) =>
    setPuppies(puppies.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const removePup = (i) => setPuppies(puppies.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!sire || !dam || !whelpDate) {
      toast.error("Select sire, dam, and whelp date");
      return;
    }
    const validPuppies = puppies.filter((p) => p.name.trim());
    if (validPuppies.length === 0) {
      toast.error("Add at least one puppy with a name");
      return;
    }
    const missingBreed = validPuppies.findIndex((p) => !(p.breed || "").trim());
    if (missingBreed !== -1) {
      toast.error(`Enter a breed for puppy #${missingBreed + 1}`);
      return;
    }
    setSaving(true);
    try {
      const litter_payload = {
        sire_id: sire,
        dam_id: dam,
        whelp_date: whelpDate,
        breeder_id: breederId === "none" ? "" : breederId,
        notes,
        puppies: validPuppies.map((p) => ({
          name: p.name,
          sex: p.sex,
          color: p.color || "",
          breed: (p.breed || "").trim(),
          dob: "",
          microchip: "",
          registration_number: "",
          sire_id: "",
          dam_id: "",
          breeder_id: "",
          current_owner: "",
          notes: "",
          photos: [],
        })),
      };
      const { data } = await api.post("/billing/checkout", {
        package_id: "litter_registration",
        origin_url: webOrigin(),
        litter_payload,
        ...(promo?.valid ? { promo_code: promo.code } : {}),
      });
      if (data.free && data.litter) {
        const reason = data.admin_override
          ? "free for admin"
          : data.promo
          ? "promo applied"
          : "plan allowance used";
        toast.success(
          `Litter registered with ${data.litter.puppy_ids.length} puppies (${reason})`
        );
        navigate(`/litters/${data.litter.id}/confirmation`);
        return;
      }
      if (data.url) {
        toast.message("Redirecting to secure checkout…");
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">New litter</p>
          <h1 className="font-spectral text-4xl tracking-tight">Register a litter</h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Purebred or exotic — every puppy gets its own Certificate of Registration, with the breed printed on it.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-8">
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
            <h2 className="font-spectral text-2xl mb-2">Parents</h2>
            <div
              className="mb-5 flex items-start gap-2.5 text-[13px] text-[#3F3F3F] bg-[#FBF7EC] border border-[#E8D9A6] rounded-lg px-3.5 py-2.5"
              data-testid="parents-required-note"
            >
              <Info size={15} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
              <span>
                Both parents must be registered in the registry before a litter can be recorded.
                Don't see them in the search?{" "}
                <Link
                  to="/dogs/new"
                  className="font-semibold underline-offset-2 hover:underline text-ink"
                  data-testid="parents-register-link"
                >
                  Register a new dog
                </Link>{" "}
                first, then come back here.
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Sire *
                </Label>
                <div className="mt-1">
                  <DogPicker
                    value={sire}
                    onChange={(idVal, obj) => { setSire(idVal); setSireObj(obj); }}
                    sexFilter="male"
                    placeholder="Search sires by name or reg #"
                    testId="litter-sire-picker"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Dam *
                </Label>
                <div className="mt-1">
                  <DogPicker
                    value={dam}
                    onChange={(idVal, obj) => { setDam(idVal); setDamObj(obj); }}
                    sexFilter="female"
                    placeholder="Search dams by name or reg #"
                    testId="litter-dam-picker"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Whelp date *
                </Label>
                <DateOfBirthInput
                  id="litter-whelp"
                  value={whelpDate}
                  onChange={setWhelpDate}
                  required
                  max={new Date().toISOString().slice(0, 10)}
                  testId="litter-whelp-input"
                  autoComplete="off"
                  ariaLabel="Whelp date"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Breeder
                </Label>
                <Select value={breederId} onValueChange={setBreederId}>
                  <SelectTrigger className="mt-1" data-testid="litter-breeder-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {breeders.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.kennel_name || b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setShowAddBreeder(true)}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em] text-ink hover:text-crimson"
                  data-testid="litter-add-breeder-btn"
                >
                  <Plus size={11} weight="bold" /> Add breeder not listed
                </button>
              </div>
            </div>
            <div className="mt-5">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Notes
              </Label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 rounded-lg"
                data-testid="litter-notes-input"
              />
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-spectral text-2xl">Puppies</h2>
              <Button
                type="button"
                onClick={addPuppy}
                variant="outline"
                className="rounded-full border-[#E5E5E5]"
                data-testid="add-puppy-btn"
              >
                <Plus size={14} className="mr-1" /> Add puppy
              </Button>
            </div>
            <p className="text-[12px] text-[#6B6B6B] mb-5 flex items-start gap-1.5">
              <Dog size={13} weight="duotone" className="text-[#6B6B6B] mt-[2px] flex-shrink-0" />
              <span>
                Breed is required on every puppy. We pre-fill it from the sire{" "}
                {defaultBreed ? <>(<strong>{defaultBreed}</strong>)</> : "once you pick one"};
                override per pup if a litter is mixed or crossbred.
              </span>
            </p>
            <div className="space-y-3">
              {puppies.map((p, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 sm:grid-cols-[1.3fr_110px_1.2fr_1fr_auto] gap-3 items-end"
                >
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Name
                    </Label>
                    <Input
                      value={p.name}
                      onChange={(e) => updatePup(i, "name", e.target.value)}
                      className="mt-1 rounded-lg"
                      data-testid={`puppy-name-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Sex
                    </Label>
                    <Select value={p.sex} onValueChange={(v) => updatePup(i, "sex", v)}>
                      <SelectTrigger className="mt-1" data-testid={`puppy-sex-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Breed *
                    </Label>
                    <div className="mt-1" data-testid={`puppy-breed-${i}`}>
                      <BreedPicker
                        value={p.breed}
                        onChange={(v) => updatePup(i, "breed", v)}
                        placeholder={defaultBreed ? `Defaults to ${defaultBreed}` : "Pick a breed…"}
                        testId={`puppy-breed-picker-${i}`}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Color
                    </Label>
                    <Input
                      value={p.color}
                      onChange={(e) => updatePup(i, "color", e.target.value)}
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removePup(i)}
                    className="p-3 text-crimson hover:bg-[#C1272D]/5 rounded-lg"
                    data-testid={`remove-puppy-${i}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Upsell — route paid-path users toward the better-value plans. */}
          {!coversLitter && (
            <ProUpsellBanner context="litter-form" />
          )}
          {!coversLitter && (
            <div
              className="rounded-xl border border-[#C9A227]/40 bg-[#FFF8E5] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              data-testid="litter-form-upsell"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-bold uppercase tracking-[0.15em] text-[#8A6A00] mb-1">
                  Breeding more than one litter a year?
                </p>
                <p className="text-[13px] text-[#0E0E0E]/85 leading-snug">
                  {isNativeApp() ? (
                    <>An active <strong>breeder plan</strong> unlocks <strong>unlimited litters</strong>{" "}
                    + unlimited dogs, plus the Dog Log.</>
                  ) : (
                    <>This litter costs <strong>$75 one-off</strong>. The{" "}
                    <strong>Pro plan</strong> ($24.99/mo) unlocks <strong>unlimited litters</strong>{" "}
                    + unlimited dogs, plus the Dog Log. Worth it if you breed twice a year.</>
                  )}
                </p>
              </div>
              {!isNativeApp() && (
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[12px] font-bold uppercase tracking-[0.12em] px-4 py-2 shrink-0 transition-colors"
                data-testid="litter-form-upgrade-link"
              >
                See plans →
              </Link>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-[#E5E5E5] rounded-xl p-4">
            {coversLitter ? (
              <p className="flex-1 text-[12px] text-[#0E0E0E]/75 leading-snug" data-testid="litter-form-plan-included">
                <strong className="text-[#8A6A00]">Included in your plan</strong> —
                no payment required. Click <em>Register litter</em> to save it.
              </p>
            ) : (
              <div className="flex-1 max-w-md">
                {!isNativeApp() && (
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
                  Promo code (optional)
                </p>
                )}
                <CompBanner className="mb-3" />
                <PromoCodeInput
                  kind="one_time_litter"
                  amount={75}
                  applied={promo}
                  onApplied={(p) => {
                    setPromo(p);
                    toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                  }}
                  onCleared={() => setPromo(null)}
                />
              </div>
            )}
            <div className="flex flex-col items-end gap-1 sm:ml-auto">
              <div className="flex flex-wrap justify-end gap-3">
              <Link to="/litters">
                <Button type="button" variant="outline" className="rounded-full border-[#E5E5E5]">
                  Cancel
                </Button>
              </Link>
              {!(isIOS() && !coversLitter && !(promo?.valid && promo.final === 0)) && (
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-8"
                data-testid="litter-submit-btn"
              >
                {saving
                  ? "Processing…"
                  : coversLitter
                  ? "Register litter (included in your plan)"
                  : promo?.valid && promo.final === 0
                  ? "Register litter (promo applied)"
                  : promo?.valid
                  ? (isNativeApp() ? `Continue to checkout in browser — $${promo.final.toFixed(2)}` : `Pay $${promo.final.toFixed(2)} & register`)
                  : (isNativeApp() ? "Continue to checkout in browser — $75" : "Pay $75 & register")}
              </Button>
              )}
              </div>
              {isIOS() && !coversLitter && !(promo?.valid && promo.final === 0) && (
                <IapConsumableButton
                  creditKey="litter_registration"
                  label="Buy litter registration"
                  noticeLabel="One-time litter registration"
                  onGranted={() => onSubmit({ preventDefault: () => {} })}
                  className="mt-1"
                />
              )}
              {isNativeApp() && !isIOS() && !coversLitter && !(promo?.valid && promo.final === 0) && (
                <p className="text-[11px] text-[#9CA3AF] mt-1 text-right" data-testid="litter-form-browser-hint">
                  Checkout opens securely in your browser
                </p>
              )}
            </div>
          </div>
        </form>
      </div>
      <AddBreederDialog
        open={showAddBreeder}
        onClose={() => setShowAddBreeder(false)}
        existingBreeders={breeders}
        onPickedExisting={(b) => setBreederId(b.id)}
        onCreated={async (b) => {
          const r = await api.get("/breeders");
          setBreeders(r.data);
          setBreederId(b.id);
        }}
      />
    </AppShell>
  );
}
